
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, 
  Plus, 
  Search, 
  Copy, 
  Trash2, 
  Edit, 
  Calendar, 
  Clock
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

interface Template {
  id: string;
  name: string;
  subject: string;
  preview_text?: string;
  content: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

interface TemplatesListProps {
  templates: Template[];
  onCreateTemplate: () => void;
  onEditTemplate: (template: Template) => void;
  onDeleteTemplate: (templateId: string) => void;
  onDuplicateTemplate: (template: Template) => void;
  isLoading: boolean;
}

export const TemplatesList: React.FC<TemplatesListProps> = ({
  templates,
  onCreateTemplate,
  onEditTemplate,
  onDeleteTemplate,
  onDuplicateTemplate,
  isLoading
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  
  const filteredTemplates = templates.filter(template => 
    template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.subject.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDelete = (templateId: string, templateName: string) => {
    if (window.confirm(`Are you sure you want to delete the template "${templateName}"?`)) {
      onDeleteTemplate(templateId);
      toast.success(`Template "${templateName}" deleted successfully`);
    }
  };

  const handleDuplicate = (template: Template) => {
    onDuplicateTemplate(template);
    toast.success(`Template "${template.name}" duplicated successfully`);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-medium text-slate-800">Email Templates</h3>
          <p className="text-sm text-slate-500">Create and manage your email templates</p>
        </div>
        <Button className="bg-slate-900 hover:bg-slate-800" onClick={onCreateTemplate}>
          <Plus className="h-4 w-4 mr-2" />
          New Template
        </Button>
      </div>
      
      <Card className="border border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <CardTitle>Email Templates</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          <CardDescription>
            Create and manage templates for your newsletter campaigns
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-72 flex items-center justify-center">
              <div className="animate-pulse text-slate-500">Loading templates...</div>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              {searchQuery ? (
                <p className="text-slate-500">No templates match your search criteria</p>
              ) : (
                <>
                  <p className="text-slate-500 mb-4">No templates have been created yet</p>
                  <Button 
                    variant="outline" 
                    className="border-slate-200" 
                    onClick={onCreateTemplate}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create your first template
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="rounded-md border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[30%]">Template Name</TableHead>
                    <TableHead className="w-[40%]">Subject Line</TableHead>
                    <TableHead className="w-[15%] text-center">Last Updated</TableHead>
                    <TableHead className="w-[15%] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTemplates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-slate-400" />
                          {template.name}
                          {!template.is_active && (
                            <Badge variant="outline" className="ml-1.5 text-xs">
                              Inactive
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 truncate max-w-xs">
                        {template.subject}
                      </TableCell>
                      <TableCell className="text-slate-500 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5" />
                          <span>{format(new Date(template.updated_at), 'MMM d, yyyy')}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onEditTemplate(template)}
                            className="h-8 w-8 text-slate-600"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDuplicate(template)}
                            className="h-8 w-8 text-slate-600"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(template.id, template.name)}
                            className="h-8 w-8 text-slate-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
